import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';

import { trackAffiliateClick } from 'api/affiliateClick';
import { Role } from 'api/types/auth';
import { ProductCategory } from 'api/types/product';

import { ProductCardBase } from 'components/ProductCard';
import { getOutOfStock } from 'components/ProductCard/utils';
import ProductCartLabel from 'components/ProductCard/ProductCartLabel';
import { EPriceType } from 'components/NewProductStepper/ProductBaseForm/constants';
import { Zendesk } from 'components/Zendesk';

import { Button, CardContent, Typography } from 'ui-kit';
import { isLocalProduct } from 'utils/product';
import { currencyToSymbol, formatCurrency } from 'utils';
import { convertPxToRem } from 'utils/utilsV2';
import { useSignedUser } from 'api/auth';

export interface IProductCardStore {
  title: string;
  quantity: number;
  id: number;
  onHandleSetModalId: ({ id }: { id: number }) => void;
  className?: string;
  currency?: string;
  price?: string;
  category?: ProductCategory | string;
  isPreorder?: boolean;
  isRequest?: boolean;
  isLink?: boolean;
  hasQuantity?: boolean;
  imgSrc?: string;
  externalLink?: string;
  storeId?: number;
  priceType: EPriceType | string;
}

const ProductCardStore = ({
  title,
  quantity,
  id,
  onHandleSetModalId,
  className,
  currency,
  price,
  category,
  isPreorder,
  isRequest,
  isLink,
  hasQuantity,
  imgSrc,
  externalLink,
  storeId,
  priceType,
}: IProductCardStore) => {
  const outOfStock = getOutOfStock(hasQuantity, quantity);
  const { data: user } = useSignedUser();

  const isGuest = user.role === Role.GUEST;
  const openExternalLink = useCallback(() => {
    const url = new URL(externalLink);
    if (isLocalProduct(category as ProductCategory)) {
      url.searchParams.append('store', storeId.toString());
      if (user?.role === Role.GUEST) {
        trackAffiliateClick({ affiliateLink: externalLink, guest: user.user_id, product: id });
      }
    }
    window.open(url.href, '_blank');
  }, [externalLink, user?.user_id, id, user?.role, storeId, category]);

  const handleCardClick = useCallback(() => {
    if (isLink) {
      openExternalLink();
      return;
    }
    onHandleSetModalId({ id });
  }, [isLink, onHandleSetModalId]);

  const handleAddProduct = useCallback(
    (e) => {
      e.stopPropagation();

      if (isLink) {
        openExternalLink();
      } else {
        onHandleSetModalId({ id });
      }
    },
    [isLink, onHandleSetModalId],
  );

  const buttonText = useMemo(() => {
    if (isLink) {
      return 'View Site';
    }

    return 'Select options';
  }, [isLink]);

  const formattedPrice = useMemo(() => {
    if (isLink) {
      return;
    }

    if (priceType === EPriceType.VARIABLE) {
      return 'Variable price';
    }

    if (priceType === EPriceType.MULTIPLE) {
      return `from ${currencyToSymbol(currency) || `${currency}`} ${formatCurrency(price)}`;
    }

    return `${currencyToSymbol(currency) || `${currency}`} ${formatCurrency(price)}`;
  }, [currency, price]);

  const productLabel = useMemo(() => {
    if (isPreorder && !isRequest) {
      return 'Pre-order';
    }

    if (isRequest) {
      return 'Request';
    }

    if (isLink) {
      return 'Link';
    }

    return undefined;
  }, [isPreorder, isRequest, isLink]);

  return (
    <div>
      <ProductCardBase
        className={clsx(className, 'ProductCardStore')}
        onClick={handleCardClick}
        imgSrc={imgSrc}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: convertPxToRem({ px: 8 }),
            minHeight: convertPxToRem({ px: 65 }),
            padding: `${convertPxToRem({ px: 10 })} ${convertPxToRem({
              px: 10,
            })} 0 ${convertPxToRem({
              px: 10,
            })}`,
          }}
        >
          <Typography
            variant="subtitle2"
            rows={2}
            style={{
              fontWeight: 700, // bold
              minHeight: convertPxToRem({ px: 45 }),
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="body1"
            color="secondary"
            className="ProductCardStore-content__details-price"
          >
            {formattedPrice}
          </Typography>
        </div>
        <CardContent>
          {!outOfStock && (
            <Button variant="contained" color="textDark" fullWidth onClick={handleAddProduct}>
              {buttonText}
            </Button>
          )}
        </CardContent>
        <div className="ProductCardStore_labels">
          {productLabel && <ProductCartLabel label={productLabel} />}
        </div>
        {outOfStock && (
          <ProductCartLabel label="Out of stock" className="ProductCard_label-outof-stock" />
        )}
      </ProductCardBase>
      <Zendesk load={isGuest} />
    </div>
  );
};

export default ProductCardStore;
